import { useState, useEffect } from 'react';
import { useLocation, useSearch } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { Eye, EyeOff, Loader2, UserRound } from 'lucide-react';
import React from 'react';
import eatzyLogo from "@/assets/eatzy-teal-disc-1024.png";

const PROVIDER_LABELS: Record<string, string> = {
  google: 'Google',
  facebook: 'Facebook',
  apple: 'Apple',
};

const SOCIAL_ONLY_ERROR_CODES = new Set([
  'SOCIAL_ONLY',
  'USE_SOCIAL_LOGIN',
  'SOCIAL_LOGIN_REQUIRED',
  'NO_PASSWORD',
  'PASSWORD_NOT_SET',
]);

export default function Login() {
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const { login: signIn, isLoading } = useAuth();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [socialProviders, setSocialProviders] = useState<string[]>([]);
  const [isCheckingMethods, setIsCheckingMethods] = useState(false);
  const [checkedEmail, setCheckedEmail] = useState('');

  const [error, setErrorState] = useState(() => {
    const stored = sessionStorage.getItem('login_error');
    return stored || '';
  });

  const setError = (msg: string) => {
    if (msg) {
      sessionStorage.setItem('login_error', msg);
    } else {
      sessionStorage.removeItem('login_error');
    }
    setErrorState(msg);
  };

  const clearError = () => {
    if (error) {
      sessionStorage.removeItem('login_error');
      setErrorState('');
    }
    setSocialProviders([]);
  };

  useEffect(() => {
    const queryString = searchString || window.location.search;
    const params = new URLSearchParams(queryString);
    const urlError = params.get('error') || params.get('auth_error');

    if (urlError) {
      const errorMessages: Record<string, string> = {
        'auth_failed': 'Authentication failed. Please try again.',
        'auth_error': 'An error occurred during authentication.',
        'auth_init_failed': 'Could not start authentication. Please try again.',
        'verification_failed': 'Token verification failed. Please try again.',
        'authentication_failed': 'Authentication failed. Please try again.',
        'login_failed': 'Login failed. Please try again.',
        'auth_not_configured': 'Authentication service is not configured. Please contact support.',
      };
      setError(errorMessages[urlError] || urlError);
      window.history.replaceState({}, '', '/login');
    }
  }, [searchString]);

  const checkAuthMethods = async (email: string): Promise<string[]> => {
    try {
      const res = await fetch(`/api/auth/check-methods?email=${encodeURIComponent(email)}`);
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data.providers) ? data.providers : [];
    } catch {
      return [];
    }
  };

  const applySocialHintFromProviders = (providers: string[], loginErrorCode?: string) => {
    const socialOnly = providers.filter(p => p !== 'password');
    if (socialOnly.length > 0 && !providers.includes('password')) {
      setSocialProviders(socialOnly);
      return true;
    }
    // Fallback: infer from sign-in error code when check-methods returns empty
    if (providers.length === 0 && loginErrorCode && SOCIAL_ONLY_ERROR_CODES.has(loginErrorCode.toUpperCase())) {
      setSocialProviders(['google', 'facebook']);
      return true;
    }
    return false;
  };

  const handleEmailBlur = async () => {
    const email = formData.email.trim();
    if (!email || email === checkedEmail) return;
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!emailValid) return;
    setCheckedEmail(email);
    const providers = await checkAuthMethods(email);
    applySocialHintFromProviders(providers);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorState('');
    setSocialProviders([]);

    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    sessionStorage.removeItem('login_error');

    const result = await signIn(formData.email, formData.password);

    if (result.success) {
      setTimeout(() => {
        if (window.location.pathname === '/login') {
          const params = new URLSearchParams(window.location.search);
          const returnUrl = params.get('returnUrl') || '/';
          window.location.href = returnUrl;
        }
      }, 500);
    } else {
      const errorCode = (result as any).code as string | undefined;
      setError(result.error || 'Login failed');

      // Check if this is a social-only account so we can show a helpful hint.
      // First check the error code for an immediate signal, then fetch check-methods.
      if (errorCode && SOCIAL_ONLY_ERROR_CODES.has(errorCode.toUpperCase())) {
        applySocialHintFromProviders([], errorCode);
      } else {
        setIsCheckingMethods(true);
        try {
          const providers = await checkAuthMethods(formData.email);
          applySocialHintFromProviders(providers, errorCode);
        } finally {
          setIsCheckingMethods(false);
        }
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearError();
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const continueWithoutLoginUrl = (() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('returnUrl') || '';
    try { return decodeURIComponent(raw); } catch { return raw; }
  })();
  const BROWSABLE_PREFIXES = ['/r/', '/restaurant/', '/restaurants', '/'];
  const canContinueWithoutLogin = continueWithoutLoginUrl.length > 0 && BROWSABLE_PREFIXES.some(p =>
    p === '/'
      ? continueWithoutLoginUrl === '/'
      : continueWithoutLoginUrl.startsWith(p)
  );

  const handleContinueWithoutLogin = () => {
    window.location.href = continueWithoutLoginUrl;
  };

  const socialProviderLabel = socialProviders.length === 1
    ? PROVIDER_LABELS[socialProviders[0]] || socialProviders[0]
    : socialProviders.map(p => PROVIDER_LABELS[p] || p).join(' or ');

  const getSocialButtonHighlight = (provider: string) =>
    socialProviders.includes(provider) ? 'ring-2 ring-primary ring-offset-1' : '';

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <img src={eatzyLogo} alt="Eatzy" className="h-14 w-14 object-contain" />
          </div>
          <CardTitle className="text-2xl font-bold">Welcome Back</CardTitle>
          <CardDescription>Sign in to your Eatzy account</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && !socialProviders.length && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {socialProviders.length > 0 && (
              <Alert>
                <AlertDescription>
                  This account uses {socialProviderLabel} sign-in. Use the{' '}
                  <strong>{socialProviderLabel}</strong> button below to continue.
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleEmailBlur}
                disabled={isLoading}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center justify-center w-10 text-muted-foreground hover:text-foreground focus:outline-none disabled:opacity-50"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                className="text-sm text-primary hover:underline"
                onClick={() => setLocation('/forgot-password')}
                disabled={isLoading}
              >
                Forgot password?
              </button>
            </div>

            <Button type="submit" className="w-full touch-manipulation" disabled={isLoading || isCheckingMethods}>
              {(isLoading || isCheckingMethods) ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Social Login Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          {/* Social Login Buttons */}
          <div className="grid grid-cols-1 gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setErrorState('');
                const params = new URLSearchParams(window.location.search);
                const returnUrl = params.get('returnUrl');
                if (returnUrl && returnUrl !== '/') sessionStorage.setItem('auth_return_url', returnUrl);
                window.location.href = '/api/auth/google';
              }}
              disabled={isLoading}
              className={`w-full ${getSocialButtonHighlight('google')}`}
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              {isLoading ? 'Connecting...' : 'Google'}
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                setErrorState('');
                const params = new URLSearchParams(window.location.search);
                const returnUrl = params.get('returnUrl');
                if (returnUrl && returnUrl !== '/') sessionStorage.setItem('auth_return_url', returnUrl);
                window.location.href = '/api/auth/facebook';
              }}
              disabled={isLoading}
              className={`w-full ${getSocialButtonHighlight('facebook')}`}
            >
              <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              Facebook
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                setErrorState('');
                const params = new URLSearchParams(window.location.search);
                const returnUrl = params.get('returnUrl');
                if (returnUrl && returnUrl !== '/') sessionStorage.setItem('auth_return_url', returnUrl);
                window.location.href = '/api/auth/apple';
              }}
              disabled={isLoading}
              className={`w-full ${getSocialButtonHighlight('apple')}`}
            >
              <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
              </svg>
              Continue with Apple
            </Button>
          </div>

          {/* Sign up link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <button
                className="p-0 h-auto font-semibold text-primary hover:underline"
                onClick={() => setLocation('/signup')}
                disabled={isLoading}
              >
                Sign up
              </button>
            </p>
          </div>

          {/* Continue without logging in — only shown when returnUrl is a public /r/ page */}
          {canContinueWithoutLogin && (
            <div className="mt-3 text-center">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground hover:underline transition-colors"
                onClick={handleContinueWithoutLogin}
                disabled={isLoading}
              >
                <UserRound className="h-3.5 w-3.5" />
                Continue without logging in
              </button>
            </div>
          )}

          {/* Legal footer */}
          <div className="mt-4 text-center space-x-3">
            <button
              className="text-xs text-muted-foreground hover:underline"
              onClick={() => setLocation('/terms')}
            >
              Terms of Service
            </button>
            <span className="text-xs text-muted-foreground">|</span>
            <button
              className="text-xs text-muted-foreground hover:underline"
              onClick={() => setLocation('/privacy')}
            >
              Privacy Policy
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
